<template>
  <modal
    name="leave-request-schedule-preview"
    :width="'92%'"
    :height="'auto'"
    :adaptive="true"
    :scrollable="true"
    class="ui-modal ui-modal--wide modal-not-overflow"
  >
    <section class="ui-modal__content" aria-labelledby="leave-request-calendar-title">
      <app-modal-header design="new" :title="readOnly ? 'Leave in Schedule' : 'Leave request in Schedule'" title-id="leave-request-calendar-title" @close="close" />
      <template v-if="request">
        <div class="ui-preview-summary">
          <div>
            <strong>{{ request.employee.full_name }}</strong>
            <span>{{ request.type.name }} · {{ request.from_date }} – {{ request.to_date }}</span>
          </div>
          <span class="ui-text-muted">{{ dates.start }} – {{ dates.end }}</span>
        </div>
        <div class="ui-preview-filters">
          <app-form-group design="new" label="Departments" label-for="leave-preview-departments">
            <app-select
              id="leave-preview-departments"
              v-model="selectedDepartments"
              design="new"
              :options="availableDepartmentOptions"
              label="name"
              placeholder="All departments"
              aria-label="Departments in preview"
              multiple
              @input="updateSchedule"
            />
          </app-form-group>
        </div>
        <div ref="calendarBody" class="ui-preview-body">
          <app-spinner v-if="loading" design="new" />
          <app-no-results v-else-if="error" design="new" title="Schedule unavailable" text="Please try again." />
          <div v-else-if="schedule" class="ui-preview-calendar">
            <app-schedule
              design="new"
              :schedule="schedule"
              :need_search="false"
              :drag-from-header-only="true"
              :stacked-period-labels="true"
              :grouped-period-months="true"
              :fill-available-height="true"
              sidebar-label="Employee / Department"
            />
          </div>
        </div>
        <footer class="ui-preview-footer">
          <a class="ui-link" :href="scheduleHref" target="_blank" rel="noopener noreferrer">Show in Schedule</a>
          <div v-if="!readOnly" class="ui-actions ui-actions--tight">
            <app-button design="new" theme="error" type="button" @click.native="choose('reject')">Reject</app-button>
            <app-button design="new" theme="success" type="button" @click.native="choose('approve')">Approve</app-button>
          </div>
        </footer>
      </template>
    </section>
  </modal>
</template>

<script>
import moment from 'moment/moment'
import AppSchedule from '@/components/AppSchedule.vue'
import { getEmployeesDepartmentList, getEmployeesLeavesCalendarV2, getEmployeesSalesLeavesCalendarV2 } from '@/http'
import { buildLeaveSchedule } from './leaveSchedule'

export default {
  name: 'LeaveRequestSchedulePreview',
  components: { AppSchedule },
  props: {
    sales: Boolean,
    readOnly: Boolean,
    departmentOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      request: null,
      dates: null,
      schedule: null,
      events: [],
      selectedDepartments: [],
      loadedDepartmentOptions: [],
      loading: false,
      error: false,
      fetchId: 0,
    }
  },
  computed: {
    availableDepartmentOptions() {
      return this.departmentOptions.length ? this.departmentOptions : this.loadedDepartmentOptions
    },
    scheduleHref() {
      if (!this.dates) return '#'
      const query = {
        from_date: this.dates.start,
        to_date: this.dates.end,
        status: this.readOnly ? 'approved' : 'all',
      }
      if (this.selectedDepartments.length === 1) {
        query.department = this.selectedDepartments[0].code
      }
      return this.$router.resolve({
        name: this.sales ? 'employees-sales-leaves-schedule' : 'employees-leaves-schedule',
        query,
      }).href
    },
  },
  created() {
    if (!this.departmentOptions.length) {
      getEmployeesDepartmentList().then(response => {
        this.loadedDepartmentOptions = Array.isArray(response.data) ? response.data : []
      })
    }
  },
  beforeDestroy() {
    this.fetchId += 1
    if (this.calendarResizeObserver) this.calendarResizeObserver.disconnect()
  },
  mounted() {
    this.calendarResizeObserver = new ResizeObserver(() => {
      if (this.schedule) this.updateSchedule()
    })
  },
  methods: {
    open(request) {
      this.request = request
      const department = request.employee && request.employee.department
      this.selectedDepartments = []
      if (department) {
        const option = this.availableDepartmentOptions.find(item => String(item.code) === String(department.code))
        this.selectedDepartments = [option || department]
      }
      const fromDate = moment(request.from_date)
      const duration = moment(request.to_date).diff(fromDate, 'days') + 1
      this.dates = {
        start: fromDate.clone().subtract(7, 'days').format('YYYY-MM-DD'),
        end: fromDate.clone().add(Math.min(duration, 14) + 6, 'days').format('YYYY-MM-DD'),
      }
      this.schedule = null
      this.events = []
      this.error = false
      this.loading = true
      this.$modal.show('leave-request-schedule-preview')
      this.$nextTick(() => {
        if (this.calendarResizeObserver && this.$refs.calendarBody) {
          this.calendarResizeObserver.observe(this.$refs.calendarBody)
        }
      })
      const fetchId = ++this.fetchId
      const fetchCalendar = this.sales ? getEmployeesSalesLeavesCalendarV2 : getEmployeesLeavesCalendarV2
      return fetchCalendar({
        from_date: this.dates.start,
        to_date: this.dates.end,
        status: this.readOnly ? 'approved' : 'all',
        department: 'all',
        position: 'all',
      })
        .then(response => {
          if (fetchId !== this.fetchId) return
          const events = Array.isArray(response.data.events) ? response.data.events : []
          const selectedId = `${this.readOnly ? 'leave' : 'request'}:${request.id}`
          events.sort((left, right) => Number(right.id === selectedId) - Number(left.id === selectedId))
          if (!this.selectedDepartments.length) {
            const selectedEvent = events.find(event => event.id === selectedId)
            const selectedDepartment = selectedEvent && selectedEvent.department
            if (selectedDepartment) {
              const option = this.availableDepartmentOptions.find(item => String(item.code) === String(selectedDepartment.code))
              this.selectedDepartments = [option || selectedDepartment]
            }
          }
          this.events = events
          this.updateSchedule()
        })
        .catch(() => { if (fetchId === this.fetchId) this.error = true })
        .finally(() => { if (fetchId === this.fetchId) this.loading = false })
    },
    close() {
      this.fetchId += 1
      this.$modal.hide('leave-request-schedule-preview')
    },
    updateSchedule() {
      const body = this.$refs.calendarBody
      const bodyStyle = getComputedStyle(body)
      const availableWidth = body.getBoundingClientRect().width - parseFloat(bodyStyle.paddingLeft) - parseFloat(bodyStyle.paddingRight)
      const dayCount = moment(this.dates.end).diff(moment(this.dates.start), 'days') + 1
      const dayWidth = Math.max(42, Math.min(82, (availableWidth - 202) / dayCount))
      const selectedDepartmentCodes = this.selectedDepartments.map(department => String(department.code))
      let events = this.events
      if (selectedDepartmentCodes.length) {
        events = events.filter(event => event.department && selectedDepartmentCodes.includes(String(event.department.code)))
      }
      this.schedule = buildLeaveSchedule(events, this.dates, dayWidth)
      const selectedId = `${this.readOnly ? 'leave' : 'request'}:${this.request.id}`
      this.schedule.events.forEach(event => {
        event.interactive = false
        if (event.id === selectedId) event.style.boxShadow = '0 0 0 3px var(--ui-accent, #1769e0)'
      })
    },
    choose(action) {
      const request = this.request
      this.close()
      this.$emit(action, request)
    },
  },
}
</script>
